/* eslint-disable react/no-multi-comp */
import {
  AccountingFeatureIcon,
  CircleCloseIcon,
  CommissionRuleFeatureIcon,
  ExternalCompanyFeatureIcon,
  ExternalLinkIcon,
  JobCostsFeatureIcon,
  JobMapFeatureIcon,
  ReportsFeatureIcon,
  WebsiteIcon,
  WhatsNewIcon
} from 'assets/icons';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import CustomModal from 'components/custom-modal/CustomModal';
import { WHATS_NEW_BLOG_POST } from 'config/index';
import { FC } from 'react';
import { VoidFn } from 'types/baseTypes';
import { openInNewTab } from 'utils/commonUtils';

interface WhatsNewModalProps {
  config: IWhatsNewConfig;
  onPrimaryBtnClick: VoidFn;
  onClose: VoidFn;
  className?: string;
}

export interface IWhatsNewConfig {
  open: boolean;
}
interface INewFeatureInfo {
  type: NewFeatureListEnum;
  title: string;
  description: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
  isWebOnly?: boolean;
}

enum NewFeatureListEnum {
  JOB_COSTS,
  COMMISSION_RULES,
  EXTERNAL_COMPANY_REFERRALS,
  REPORTS,
  JOB_MAP_VIEWS,
  ACCOUNTING_PAY_RUNS
}

const newFeatureInfoList: INewFeatureInfo[] = [
  {
    type: NewFeatureListEnum.JOB_COSTS,
    title: 'Job costs and expenses',
    description: 'Track job profitability with line item costs and expenses',
    icon: JobCostsFeatureIcon
  },
  {
    type: NewFeatureListEnum.COMMISSION_RULES,
    title: 'Commission rules',
    description: 'Define how you reward your team with flexible rules',
    icon: CommissionRuleFeatureIcon
  },
  {
    type: NewFeatureListEnum.EXTERNAL_COMPANY_REFERRALS,
    title: 'External company referrals',
    description: 'Track your referrals and marketing partners with external companies',
    icon: ExternalCompanyFeatureIcon
  },
  {
    type: NewFeatureListEnum.REPORTS,
    title: 'Reports',
    description: 'Deep dive into the state of your business, payments, and external company referrals',
    icon: ReportsFeatureIcon,
    isWebOnly: true
  },
  {
    type: NewFeatureListEnum.JOB_MAP_VIEWS,
    title: 'Job map views',
    description: 'Streamline your dispatching with map, saved views, and job tags',
    icon: JobMapFeatureIcon,
    isWebOnly: true
  },
  {
    type: NewFeatureListEnum.ACCOUNTING_PAY_RUNS,
    title: 'Accounting and pay runs',
    description: 'Quickly complete end of period accounting for your team with pay runs',
    icon: AccountingFeatureIcon,
    isWebOnly: true
  }
];

interface FeatureInfoItemProps {
  featureInfo: INewFeatureInfo;
  className?: string;
}

const FeatureInfoItem: FC<FeatureInfoItemProps> = ({ featureInfo, className = '' }) => {
  return (
    <div className={`${className} flex`}>
      <featureInfo.icon className="mr-5 shrink-0" />
      <div className="flex flex-col">
        <div className="mb-1 flex items-center text-17px font-semibold text-heading">
          {featureInfo.title}
          {featureInfo.isWebOnly && (
            <div
              className="ml-2 flex items-center rounded-md border border-borderGray
               bg-secondaryBtn px-[6px] py-1 text-xs font-medium text-tertiaryText">
              <WebsiteIcon className="path path-fill-current mr-1 h-4 w-4 text-tertiaryText" />
              Web only
            </div>
          )}
        </div>
        <div className="text-sbase font-normal text-primaryText">{featureInfo.description}</div>
        <div></div>
      </div>
    </div>
  );
};

const WhatsNewModal: FC<WhatsNewModalProps> = ({ onPrimaryBtnClick, config, onClose = undefined, className = '' }) => {
  return (
    <div className={`${className}`}>
      <CustomModal
        enableOutsideClick={false}
        closeModal={onClose && onClose}
        open={config.open}
        backgroundColor="bg-primary"
        height="max-h-[90%] h-[678px]"
        width="w-[395px] max-w-[482px] w-[90%] md:w-[482px]"
        className="overflow-auto">
        <div className="flex h-full w-full grow flex-col">
          <CircleCloseIcon className="absolute top-2 right-2 z-[1] shrink-0 cursor-pointer" onClick={onClose} />
          <div className="customTransparentScroll flex h-full w-full flex-col overflow-auto px-10 pt-[33px] pb-6">
            <WhatsNewIcon className="mx-auto mb-4 h-[60px] w-[60px] shrink-0" />
            <div className="mx-auto mb-4 text-xl font-bold text-heading">What's New</div>
            <div
              className="customNormalScroll flex max-h-[400px] min-h-[400px] flex-col space-y-4 
            overflow-auto rounded-md border px-3 py-4">
              {newFeatureInfoList.map(featureInfo => (
                <FeatureInfoItem featureInfo={featureInfo} key={featureInfo.type} />
              ))}
            </div>
            <div
              onClick={() => openInNewTab(WHATS_NEW_BLOG_POST)}
              className="mx-auto mt-5 flex cursor-pointer items-center">
              <span className="text-sbase font-semibold text-accentText">Read the&nbsp;</span>
              <span className="mr-2 text-sbase font-semibold text-secondary">blog post</span>
              <ExternalLinkIcon className="shrink-0" />
            </div>
            <CustomButton
              className="mt-6 h-[38px] w-full shrink-0"
              id="whats-new-primary-btn"
              type={ButtonType.PRIMARY}
              onClick={onPrimaryBtnClick}>
              Got it
            </CustomButton>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default WhatsNewModal;
